"use client";

import React, { useState } from "react";
import InputHolder from "@/components/forms/inputHolder";
import SelectPill from "@/components/creator/SelectPill";
import ChipGroup from "@/components/creator/ChipGroup";
import {
  AGE_RANGES,
  GENDERS,
  OCCUPATIONS,
  LANGUAGES,
  COUNTRIES,
  MY_STATES,
} from "@/app/constants/creatorNetwork";
import type { CreatorApplication } from "@/types/creatorNetwork";

type StepProps = {
  data: CreatorApplication;
  onUpdate: (patch: Partial<CreatorApplication>) => void;
  errors: Record<string, string | null | undefined>;
};

const MY_STATE_VALUES = new Set(MY_STATES.map((s) => s.value));

function deriveCountry(stateRegion: string): string {
  if (stateRegion === "Singapore") return "Singapore";
  if (MY_STATE_VALUES.has(stateRegion)) return "Malaysia";
  return "";
}

export default function StepAbout({ data, onUpdate, errors }: StepProps) {
  const [country, setCountry] = useState<string>(() => deriveCountry(data.stateRegion));
  return (
    <div>
      <h3 className="step-title text-white text-2xl font-bold mb-1">About you</h3>
      <p className="text-white/60 mb-6">Basic details so we know who&apos;s applying.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6">
        <div className="relative w-full sm:-ml-4 sm:w-[calc(100%+1rem)]">
          <InputHolder
            input={{ inputType: "text", name: "fullName", label: "Full name" }}
            value={data.fullName}
            onUpdate={(name: string, value: string) => onUpdate({ [name]: value })}
          />
          {errors.fullName && (
            <div className="mt-1 pl-4 text-red text-sm">{errors.fullName}</div>
          )}
        </div>

        <div className="relative w-full sm:w-[calc(100%+1rem)]">
          <InputHolder
            input={{ inputType: "tel", name: "mobile", label: "Mobile / WhatsApp" }}
            value={data.mobile}
            onUpdate={(name: string, value: string) => onUpdate({ [name]: value })}
          />
          {errors.mobile && (
            <div className="mt-1 pl-4 text-red text-sm">{errors.mobile}</div>
          )}
        </div>

        <div className="relative w-full md:col-span-2 sm:-ml-4 sm:w-[calc(100%+1rem)] md:w-[calc(50%+1rem)]">
          <InputHolder
            input={{ inputType: "email", name: "email", label: "Email" }}
            value={data.email}
            onUpdate={(name: string, value: string) => onUpdate({ [name]: value })}
          />
          {errors.email && (
            <div className="mt-1 pl-4 text-red text-sm">{errors.email}</div>
          )}
        </div>  

        <div className="sm:pl-0 sm:pr-4">
          <SelectPill
            name="country"
            value={country}
            onChange={(v) => {
              setCountry(v);
              onUpdate({ stateRegion: v === "Singapore" ? "Singapore" : "" });
            }}
            options={COUNTRIES}
            placeholder="Country"
          />
          {errors.stateRegion && !country && (
            <div className="mt-1 pl-4 text-red text-sm">{errors.stateRegion}</div>
          )}
        </div>

        {country === "Malaysia" && (
          <div className="sm:pl-4 sm:pr-0">
            <SelectPill
              name="stateRegion"
              value={data.stateRegion}
              onChange={(v) => onUpdate({ stateRegion: v })}
              options={MY_STATES}
              placeholder="State"
            />
            {errors.stateRegion && (
              <div className="mt-1 pl-4 text-red text-sm">{errors.stateRegion}</div>
            )}
          </div>
        )}
      </div>

      <div className="mt-6">
        <ChipGroup
          label="Age range"
          options={AGE_RANGES}
          mode="single"
          value={data.ageRange}
          onChange={(v) => onUpdate({ ageRange: v as string })}
          error={errors.ageRange}
        />
      </div>

      <div className="mt-6">
        <ChipGroup
          label="Gender"
          options={GENDERS}
          mode="single"
          value={data.gender}
          onChange={(v) => onUpdate({ gender: v as string })}
          error={errors.gender}
        />
      </div>

      <div className="mt-6">
        <ChipGroup
          label="Occupation / life stage"
          options={OCCUPATIONS}
          mode="single"
          value={data.occupation}
          onChange={(v) => onUpdate({ occupation: v as string })}
          otherValue={data.occupationOther}
          onOtherChange={(v) => onUpdate({ occupationOther: v })}
          error={errors.occupation}
        />
      </div>

      <div className="mt-6">
        <ChipGroup
          label="Languages you can create in"
          hint="Select all that apply"
          options={LANGUAGES}
          mode="multi"
          value={data.languages}
          onChange={(v) => onUpdate({ languages: v as string[] })}
          otherValue={data.languagesOther}
          onOtherChange={(v) => onUpdate({ languagesOther: v })}
          error={errors.languages}
        />
      </div>
    </div>
  );
}
